import { useState } from "react";
import { ImageOff } from "lucide-react";
import { useAsset } from "@/hooks/use-asset";
import { defaultWidth } from "@/constants/image";
import { cn } from "@/lib/utils";

interface PizzaThumbnailProps {
  pizzaId: string;
  /** evaluation.metadata.pizza_roi [x, y, w, h] in source pixels: the thumbnail is cropped to the pizza */
  roi?: number[] | null;
  /** size of the requested image in percent of the source (the crop then needs fewer pixels) */
  scale?: number;
  /** width / height of the box; the crop covers it around the pizza */
  aspect?: number;
  className?: string;
  alt?: string;
  /** kept off-screen thumbnails out of the request queue */
  enabled?: boolean;
}

/**
 * Small photo of a pizza for lists and tiles: a downscaled source image cropped to the pizza with CSS
 * (no extra backend crop request), the whole frame when the evaluation found no pizza.
 */
export function PizzaThumbnail({
  pizzaId,
  roi,
  scale = 10,
  aspect = 1,
  className,
  alt = "",
  enabled = true,
}: PizzaThumbnailProps) {
  const { src, isLoading } = useAsset({ pizzaId, scale, enabled: enabled && !!pizzaId });
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [failed, setFailed] = useState(false);

  // full-frame width in source pixels (the downscaled image keeps the proportions; the height follows)
  const frameW = natural ? (natural.w * 100) / scale : defaultWidth;

  let style: React.CSSProperties | undefined;
  if (roi && roi.length === 4 && roi[2] > 0 && roi[3] > 0) {
    const [rx, ry, rw, rh] = roi;
    const pad = 1.06;
    let cw: number;
    let ch: number;
    if (rw / rh > aspect) {
      cw = rw * pad;
      ch = cw / aspect;
    } else {
      ch = rh * pad;
      cw = ch * aspect;
    }
    const left = rx + rw / 2 - cw / 2;
    const top = ry + rh / 2 - ch / 2;
    style = {
      position: "absolute",
      maxWidth: "none",
      width: `${(frameW / cw) * 100}%`,
      left: `${(-left / cw) * 100}%`,
      top: `${(-top / ch) * 100}%`,
    };
  }

  return (
    <div
      className={cn("relative overflow-hidden bg-muted", className)}
      style={{ aspectRatio: aspect }}
    >
      {src && !failed ? (
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={(event) =>
            setNatural({
              w: event.currentTarget.naturalWidth,
              h: event.currentTarget.naturalHeight,
            })
          }
          onError={() => setFailed(true)}
          className={cn(
            "select-none transition-opacity duration-300",
            style ? "" : "absolute inset-0 h-full w-full object-cover",
            natural ? "opacity-100" : "opacity-0",
          )}
          style={style}
        />
      ) : !isLoading || failed ? (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/60">
          <ImageOff className="h-1/3 max-h-6 w-1/3 max-w-6" />
        </div>
      ) : null}
      {(isLoading || (src && !natural && !failed)) && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
    </div>
  );
}
